/**
 * Complete Astrological Profile — flagship one-PDF report combining
 * every v2 chapter for a single chart. Birth-data form → backend
 * orchestrates 12+ sub-services concurrently → returns big JSON →
 * we either show a tiny inline summary or trigger the PDF download
 * straight away.
 *
 * No two-column / multi-tabbed UI here on purpose: this page is a
 * one-button flow. Detail browsing happens in the standalone report
 * pages.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

interface CompleteProfileResponse {
  rules_version: string;
  input: { name: string; dob: string; tob: string };
  lagna: { rashi_name_en: string; rashi_name_hi: string; rashi_deg: number };
  chart_meta: {
    fired_yogas: { yoga_id: string; name_en: string; name_hi: string }[];
    yogkaraka_planets: string[];
    active_dasha: { md_lord_id: string | null; ad_lord_id: string | null };
  };
  career?: { direction?: { family_name_en: string; family_name_hi: string; confidence: string } };
  health?: { takeaways_en?: string[]; takeaways_hi?: string[] };
  varshaphal?: { overall_summary_en: string; overall_summary_hi: string; forecast_year: number };
  soft_errors: { chapter: string; error: string; message: string }[];
}

export function CompleteProfilePage({ onBack }: { onBack: () => void }) {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [chart, setChart] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<CompleteProfileResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.completeProfile(req) as Promise<CompleteProfileResponse>,
  });

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    if (reportMut.error instanceof ApiError && reportMut.error.status === 422) {
      return t('अमान्य इनपुट — चार्ट सत्यापित करें', 'Invalid input — verify the chart');
    }
    return reportMut.error.message ?? 'Error';
  }, [reportMut.error, lang]);

  const onSubmit = useCallback(() => {
    if (chart) reportMut.mutate(chart);
  }, [chart, reportMut]);

  const downloadPdf = useCallback(async () => {
    if (!chart) return;
    try {
      setPdfDownloading(true);
      const blob = await api.completeProfilePdfBlob(chart, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `complete_profile_${chart.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [chart, lang, t]);

  const report = reportMut.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button"
        onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>

      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            📜 {t('सम्पूर्ण ज्योतिषीय रिपोर्ट', 'Complete Astrological Profile')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'एक ही दस्तावेज़ में सब-कुछ — जन्म-कुण्डली, करियर, धन, स्वास्थ्य, वर्षफल, कर्म-पथ, सन्तान, विद्या, मंगल, विसा, क़ानूनी एवं अधिक। ~30 पृष्ठ।',
              'Everything in one bound PDF — birth chart, career, wealth, health, varshaphal, karmic path, progeny, vidya, manglik, visa, legal, and more. ~30 pages.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
          <button type="button" onClick={() => setLang('hi')}
            className={'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'hi' ? 'bg-saffron-600 text-stone-950' : 'bg-stone-800/60 text-stone-300')}>
            हिन्दी
          </button>
          <button type="button" onClick={() => setLang('en')}
            className={'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'en' ? 'bg-saffron-600 text-stone-950' : 'bg-stone-800/60 text-stone-300')}>
            English
          </button>
        </div>
      </header>

      <section className="rounded-2xl bg-stone-900/40 p-3 ring-1 ring-stone-800">
        <h3 className="mb-2 text-sm font-semibold text-saffron-300">
          {t('जन्म विवरण', 'Birth Details')}
        </h3>
        <ChartInputForm onSubmit={(req) => setChart(req)} isSubmitting={false} />
        {chart && (
          <p className="mt-2 text-xs text-emerald-400">
            ✓ {t('दर्ज', 'Saved')}: {chart.name}
          </p>
        )}
      </section>

      <div className="flex gap-3">
        <button
          type="button"
          disabled={!chart || reportMut.isPending}
          onClick={onSubmit}
          className="flex-1 rounded-md bg-saffron-600 px-4 py-3 font-semibold text-stone-950 hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
        >
          {reportMut.isPending
            ? t('गणना हो रही है… (60-90 सेकंड)', 'Computing… (60-90 seconds)')
            : t('रिपोर्ट तैयार करें', 'Generate report')}
        </button>
        <button
          type="button"
          disabled={!chart || pdfDownloading}
          onClick={downloadPdf}
          className="rounded-md bg-stone-800 px-4 py-3 text-sm font-medium text-saffron-200 ring-1 ring-saffron-700/50 hover:bg-stone-700 disabled:opacity-50"
        >
          {pdfDownloading ? t('PDF बन रहा है…', 'Building PDF…') : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </div>

      {submitError && (
        <p className="rounded-2xl bg-rose-950/60 p-4 text-rose-200 ring-1 ring-rose-900">
          {submitError}
        </p>
      )}

      {report && (
        <article className="space-y-4">
          <section className="rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
            <p className="text-xs uppercase tracking-wider text-saffron-300">
              {t('रिपोर्ट तैयार', 'Report ready')}
            </p>
            <p className="mt-1 text-base font-medium text-stone-200">
              {report.input.name} · {report.input.dob}
            </p>
            <p className="mt-1 text-xs text-stone-400">
              {t('लग्न', 'Lagna')}: {lang === 'hi' ? report.lagna.rashi_name_hi : report.lagna.rashi_name_en} ({report.lagna.rashi_deg.toFixed(2)}°)
              {report.chart_meta.active_dasha.md_lord_id && (
                <> · {t('वर्तमान महादशा', 'Current Mahā-daśā')}: <span className="capitalize">{report.chart_meta.active_dasha.md_lord_id}</span></>
              )}
            </p>
          </section>

          <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-saffron-300">
              {t('अध्याय-झलक', 'Chapter snapshot')}
            </h3>
            <ul className="space-y-2 text-sm text-stone-200">
              {report.career?.direction && (
                <li>
                  <strong>{t('करियर', 'Career')}:</strong>{' '}
                  {lang === 'hi' ? report.career.direction.family_name_hi : report.career.direction.family_name_en}
                  {' '}<span className="text-xs text-stone-500">({report.career.direction.confidence} confidence)</span>
                </li>
              )}
              {report.varshaphal && (
                <li>
                  <strong>{t('वर्षफल', 'Varshaphal')} {report.varshaphal.forecast_year}:</strong>{' '}
                  {lang === 'hi' ? report.varshaphal.overall_summary_hi : report.varshaphal.overall_summary_en}
                </li>
              )}
              {report.health?.takeaways_en && report.health.takeaways_en.length > 0 && (
                <li>
                  <strong>{t('स्वास्थ्य', 'Health')}:</strong>{' '}
                  {lang === 'hi' ? report.health.takeaways_hi?.[0] : report.health.takeaways_en?.[0]}
                </li>
              )}
              {report.chart_meta.fired_yogas.length > 0 && (
                <li>
                  <strong>{t('सक्रिय योग', 'Fired yogas')}:</strong>{' '}
                  {report.chart_meta.fired_yogas.slice(0, 5).map((y) =>
                    lang === 'hi' ? y.name_hi : y.name_en).join(', ')}
                  {report.chart_meta.fired_yogas.length > 5 && (
                    <span className="text-xs text-stone-500">
                      {' '}({t('एवं', 'and')} {report.chart_meta.fired_yogas.length - 5} {t('अधिक', 'more')})
                    </span>
                  )}
                </li>
              )}
            </ul>
            <p className="mt-4 text-xs italic text-stone-500">
              {t(
                'सम्पूर्ण विश्लेषण के लिए ऊपर "PDF डाउनलोड" दबाएँ — 12 अध्यायों का पूरा दस्तावेज़।',
                'For the full analysis, click "Download PDF" above — 12-chapter bound document.',
              )}
            </p>
          </section>

          {report.soft_errors.length > 0 && (
            <section className="rounded-2xl bg-amber-950/30 p-4 ring-1 ring-amber-900/50">
              <p className="text-xs font-semibold text-amber-200">
                ⚠ {t('कुछ अध्याय गणना नहीं हो सके', 'Some chapters could not be computed')}: {report.soft_errors.map((e) => e.chapter).join(', ')}
              </p>
            </section>
          )}
        </article>
      )}
    </div>
  );
}
